import '../../datatables/tags-sort';
import displayDatatablesPagination from '../shared/datatables_pagination';
import { withSaveState } from '../shared/datatables_state';
import {
  overridePaginationClasses,
  customizeDOMPaginationClasses
} from '../../datatables/customization-v2';
import {
  setup as tableSetup,
  setupPaginationOnDOM,
  customizePageLength,
  evaluateSelectedRows,
  defaultTableOptions
} from '../../datatables/table-setup';

export const LISTS_TABLE_SELECTOR = '.events-table';

const EventsTable = {
  start() {
    const $table = $(`${LISTS_TABLE_SELECTOR}:not(.dataTable)`);
    if (!$table.length) {
      return;
    }
    this.setupDataTable($table);
  },

  tearDown() {
    $(`${LISTS_TABLE_SELECTOR}.dataTable`).DataTable().destroy();
  },

  _toggleEventCompleted(event, table, statusColIndex) {
    const isChecked = $(event.currentTarget)[0].checked;
    const regExSearch = '^Pending|Active$';
    if (isChecked) {
      table.columns().search('').draw();
    } else {
      table.columns(statusColIndex).search(regExSearch, true, false, false).draw();
    }
  },

  _orderTiles($tableContainer, ids) {
    const $tilesContainer = $tableContainer.find('.js-tiles-in-progress,.js-tiles-upcoming,.js-tiles-completed');
    const $blankSlate = $tableContainer.find('.js-blank-slate').children();
    $tilesContainer.empty();
    const $cards = $tableContainer.find('.js-all-events-tiles');
    const $inProgressTilesContainer = $tableContainer.find('.js-tiles-in-progress');
    const $upcomingTilesContainer = $tableContainer.find('.js-tiles-upcoming');
    const $completedTilesContainer = $tableContainer.find('.js-tiles-completed');
    const $inProgressSubtitle = $tableContainer.find('.js-subtitle-in-progress');
    const $upcomingSubtitle = $tableContainer.find('.js-subtitle-upcoming');
    const $completedSubtitle = $tableContainer.find('.js-subtitle-completed');

    ids.each((id) => {
      const $card = $cards.find(`.js-event-card[data-id="${id}"]`).first().clone(true);
      switch ($card.data('status')) {
        case 'Active':
          $inProgressTilesContainer.append($card);
          break;
        case 'Pending':
          $upcomingTilesContainer.append($card);
          break;
        case 'Completed':
          $completedTilesContainer.append($card);
          break;
        default:
          break;
      }
    });
    const inProgressEventsCount = $inProgressTilesContainer.find('.js-event-card').length;
    const upcomingEventsCount = $upcomingTilesContainer.find('.js-event-card').length;
    const completedEventsCount = $completedTilesContainer.find('.js-event-card').length;
    $inProgressSubtitle.text(`${$inProgressSubtitle.data('subtitle')} (${inProgressEventsCount})`);
    $upcomingSubtitle.text(`${$upcomingSubtitle.data('subtitle')} (${upcomingEventsCount})`);
    $completedSubtitle.text(`${$completedSubtitle.data('subtitle')} (${completedEventsCount})`);
    if (inProgressEventsCount === 0) {
      const $blankSlateClone = $blankSlate.clone();
      $inProgressTilesContainer.append($blankSlateClone);
    }
    if (upcomingEventsCount === 0) {
      const $blankSlateClone = $blankSlate.clone();
      $upcomingTilesContainer.append($blankSlateClone);
    }
    if (completedEventsCount === 0) {
      const $blankSlateClone = $blankSlate.clone();
      $completedTilesContainer.append($blankSlateClone);
    }
  },

  _renderTiles(event) {
    const $table = $(event.currentTarget);
    const $tableContainer = $table.closest('.js-events-table-container');
    const table = $table.dataTable();
    const ids = table.api().rows({ order: 'current', page: 'all', search: 'applied' }).ids();
    this._orderTiles($tableContainer, ids);
    $(document).trigger('lazyload:update');
  },

  _updateItemsCounter(event) {
    const $table = $(event.currentTarget);
    const tableApi = $table.dataTable().api();
    const $totalItems = $table.closest('.js-events-table-container').find('[data-total-items]');
    const totalItemsLabel = $totalItems.data('totalItems');
    const itemsWithSearchCount = tableApi.rows({ order: 'current', page: 'all', search: 'applied' })
      .count();
    const allItemsCount = tableApi.rows({ order: 'current', page: 'all' }).count();
    if (itemsWithSearchCount === allItemsCount) {
      $totalItems.text(totalItemsLabel);
    } else {
      $totalItems.text(`${itemsWithSearchCount} of ${totalItemsLabel}`);
    }
  },

  setupDataTable($table) {
    overridePaginationClasses();
    const $checkAllCheckbox = $('#checkall');
    let table;
    // Tags column is optional; when present every column index after Event
    // Name shifts by one.  Resolve indexes from the rendered header so the
    // configuration stays correct in both shapes.
    const tagsColIndex = $table.find('th.js-tags-col').index();
    const hasTags = tagsColIndex >= 0;
    const shift = hasTags ? 1 : 0;
    const idx = {
      checkbox: 0,
      name: 1,
      tags: hasTags ? 2 : -1,
      published: 2 + shift,
      startDate: 3 + shift,
      attending: 4 + shift,
      status: 5 + shift,
      eventParts: 6 + shift,
      lastModified: 7 + shift
    };

    const columnDefs = [
      { targets: [idx.checkbox], orderable: false, width: '7%' },
      { targets: [idx.name], searchable: true, width: '20%' },
      { targets: [idx.status], searchable: true },
      { targets: [-1], orderable: false, width: '9%' },
      { targets: '_all', searchable: false }
    ];
    if (hasTags) columnDefs.push({ targets: [idx.tags], type: 'tags', width: '15%' });

    const selectedTagIds = new Set();
    $.fn.dataTable.ext.search.push((settings, _data, index) => {
      if (settings.nTable !== $table[0]) return true;
      if (selectedTagIds.size === 0) return true;
      const rowNode = table.row(index).node();
      const rawIds = ($(rowNode).data('tag-ids') || '').toString();
      const rowTagIds = rawIds.split(',').filter(Boolean);
      return rowTagIds.some(id => selectedTagIds.has(id));
    });

    // Persist the tag filter selection via DataTables' save/load state.  Bind
    // before .DataTable() so the initial stateLoadParams event during init
    // is caught and we can restore checkboxes before the first draw.
    $table.on('stateSaveParams.dt', (e, settings, data) => {
      data.selectedTagIds = Array.from(selectedTagIds);
    });
    $table.on('stateLoadParams.dt', (e, settings, data) => {
      if (!Array.isArray(data.selectedTagIds) || data.selectedTagIds.length === 0) return;
      selectedTagIds.clear();
      data.selectedTagIds.forEach(id => selectedTagIds.add(String(id)));
      selectedTagIds.forEach(id => {
        $(`.js-tag-filter-check[data-tag-id="${id}"]`).prop('checked', true);
      });
      this._updateTagFilterUI(selectedTagIds.size);
    });

    const tableOptions = {
      ...defaultTableOptions,
      order: [[idx.lastModified, 'desc']],
      dom: 'rtp',
      select: {
        style: 'multi',
        selector: 'label.js-checkbox-for-row',
        className: 'is-selected'
      },
      columnDefs,
      drawCallback() {
        customizePageLength(this);
        customizeDOMPaginationClasses();
        evaluateSelectedRows(table, $checkAllCheckbox);
        displayDatatablesPagination(this);
      },
      initComplete: () => {
        setupPaginationOnDOM();
        this.watchTagFilter(table, selectedTagIds);
      },
      language: {
        ...defaultTableOptions.language,
        zeroRecords: '<span class="alert alert--info">Sorry, no matching Events were found</span>',
        emptyTable: '<span class="alert alert--info">No Events available</span>'
      }
    };
    const tableOptionsWithSaveState = withSaveState($table, tableOptions);
    table = $table.DataTable(tableOptionsWithSaveState);
    table.on('draw', this._renderTiles.bind(this));
    table.on('draw', this._updateItemsCounter.bind(this));
    const $completedToggle = $table
      .closest('.js-events-table-container')
      .find('.js-toggle-event-completed');
    table.on('stateLoaded.dt', function (e, settings, data) {
      const statusColumn = data.columns[idx.status];
      const { search: { search } } = statusColumn;
      const hideCompleted = search === '^Pending|Active$';
      if (hideCompleted) {
        $completedToggle[0].checked = false;
      }
    });
    $completedToggle
      .on('change', (event) => this._toggleEventCompleted(event, table, idx.status));
    tableSetup(table);
  },

  watchTagFilter(table, selectedTagIds) {
    $('.js-tag-filter-dropdown .dropdown__list').on('click', (e) => {
      e.stopPropagation();
    });

    $('.js-tag-filter-check').on('change', (e) => {
      const tagId = String($(e.currentTarget).data('tag-id'));
      if (e.currentTarget.checked) {
        selectedTagIds.add(tagId);
      } else {
        selectedTagIds.delete(tagId);
      }
      this._updateTagFilterUI(selectedTagIds.size);
      table.state.save();
      table.draw();
    });

    $('.js-tag-filter-clear').on('click', (e) => {
      e.preventDefault();
      selectedTagIds.clear();
      $('.js-tag-filter-check').prop('checked', false);
      this._updateTagFilterUI(0);
      table.state.save();
      table.draw();
    });
  },

  _updateTagFilterUI(count) {
    $('.js-tag-filter-counter').text(count).toggleClass('is-hidden', count === 0);
    $('.js-tag-filter-clear').toggleClass('is-hidden', count === 0);
  }
};

export default EventsTable;
